import { Box } from '@mui/material';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
    chartDataSectionContentSx,
    chartDataSectionHeaderSx,
    chartDataSectionSummaryBannerSx,
    chartDataSectionSx,
    srOnly,
} from '../styles/chartReader.styles';
import {
    ChartDataSectionHeaderSubtitleTypography,
    ChartDataSectionHeaderTitleTypography,
} from '../styles/chartReader.typography';

type ChartDataSectionShellProps = {
    /** First line in the dark navy header band — e.g. "Apple Inc. (AAPL) — Performance of $10,000". */
    title: string;
    /** Second line under the title — e.g. "ETF · Report date: Dec 1, 2025". */
    subtitle: string;
    /** Plain-text sentence. Used verbatim by the NVDA live region and — if
     *  `summaryHTML` is not provided — also rendered in the visual banner.
     *  Changes here trigger a live-region re-announcement. */
    summary: string;
    /** Optional richer-formatting version of `summary` for visual display only
     *  (e.g. with <strong> around key values). The NVDA live region always uses
     *  the plain `summary` so tag names aren't read aloud. Inputs MUST come
     *  from trusted internal formatters — never user-controlled content — since
     *  this is spliced via `dangerouslySetInnerHTML`. */
    summaryHTML?: string;
    /** Chart-type-specific controls (e.g. Perf10kDataPointSelector). Rendered
     *  in the slot below the summary banner. */
    children?: ReactNode;
};

/**
 * Visual shell shared by every chart type. Owns three concerns:
 *   1. Layout — outer card, dark navy header, light-blue summary banner, content slot.
 *   2. Announcements — single assertive live region announces
 *      "{title}. {subtitle}. {summary}. To read a specific data point, please
 *      press tab key." on mount, then just the new summary whenever the parent
 *      updates the `summary` prop.
 *   3. Focus discipline — focus stays on the Load Chart button while the live
 *      region speaks; a single Tab carries the user into the first control
 *      inside the children slot.
 *
 * Chart-type-specific orchestrators (Perf10kChartPanel, etc.) provide title/
 * subtitle/summary computed from their own response shape and render their
 * controls as this component's children. ChartDataSection (the dispatcher)
 * picks which orchestrator runs based on the selected chart type.
 */
export function ChartDataSectionShell({ title, subtitle, summary, summaryHTML, children }: ChartDataSectionShellProps) {
    const [announcement, setAnnouncement] = useState('');
    // Tracks the summary string we've already announced. First run (ref === null)
    // fires the full intro; subsequent runs only re-announce when the summary
    // actually changes. Comparing the value (not a boolean flag) makes this
    // StrictMode-safe — dev-mode's synthetic remount sees the same summary
    // already recorded and no-ops instead of clobbering the intro with a
    // summary-only re-announcement that NVDA would catch only intermittently.
    const lastAnnouncedSummary = useRef<string | null>(null);

    useEffect(() => {
        if (lastAnnouncedSummary.current === null) {
            lastAnnouncedSummary.current = summary;
            setAnnouncement(
                `${title}. ${subtitle}. ${summary} To read a specific data point, please press tab key. You can also press Ctrl + F key to search new security at any time in site`,
            );
            return;
        }
        if (lastAnnouncedSummary.current === summary) return;
        lastAnnouncedSummary.current = summary;
        // Clear first so NVDA re-announces even when the new summary looks
        // identical to the previous one (rare, but possible with same-day reselects).
        setAnnouncement('');
        const id = setTimeout(() => setAnnouncement(summary), 50);
        return () => clearTimeout(id);
    }, [summary, title, subtitle]);

    return (
        <Box sx={chartDataSectionSx}>
            {/* Single live region — NVDA reads whatever lands here */}
            <div aria-live='assertive' aria-atomic='true' style={srOnly}>
                {announcement}
            </div>

            {/* Header — same shape for every chart type */}
            <Box sx={chartDataSectionHeaderSx}>
                <h2 style={{ margin: 0 }}>
                    <ChartDataSectionHeaderTitleTypography>{title}</ChartDataSectionHeaderTitleTypography>
                </h2>
                <ChartDataSectionHeaderSubtitleTypography>{subtitle}</ChartDataSectionHeaderSubtitleTypography>
            </Box>

            {/* Summary banner — visual only; the live region above handles speech.
                Render the richer HTML version if the caller provided one, otherwise
                fall back to the plain summary text. */}
            <Box sx={chartDataSectionSummaryBannerSx}>
                {summaryHTML ? (
                    <span dangerouslySetInnerHTML={{ __html: summaryHTML }} />
                ) : (
                    summary
                )}
            </Box>

            {/* Chart-type-specific content slot */}
            <Box sx={chartDataSectionContentSx}>{children}</Box>
        </Box>
    );
}
